import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { ContactInterface } from '../types';

interface MergeContactsModalProps {
  open: boolean;
  onClose: () => void;
  contacts: ContactInterface[];
  onMerge: (masterId: string, otherId: string) => Promise<void>;
}

function ContactCard({ contact }: { contact: ContactInterface }) {
  return (
    <Paper sx={{ p: 2, minWidth: 250 }}>
      <Typography variant="subtitle1">{contact.name}</Typography>
      <Typography variant="body2">גיל: {contact.age ?? 'N/A'}</Typography>
      <Typography variant="body2">שם הורה: {contact.parent_name}</Typography>
      <Typography variant="body2">טלפון: {contact.phone}</Typography>
      <Typography variant="body2">אימייל: {contact.email}</Typography>
      <Typography variant="body2">נוצר ב: {new Date(contact.created_at).toLocaleDateString()}</Typography>
    </Paper>
  );
}

const MergeContactsModal = ({ open, onClose, contacts, onMerge }: MergeContactsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open) return null;
  if (contacts.length !== 2) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Merge Contacts</DialogTitle>
        <DialogContent>
          <Alert severity="error">Please select exactly two contacts to merge.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  const [left, right] = contacts;
  const handleMerge = async (masterId: string, otherId: string) => {
    setLoading(true);
    setError(null);
    try {
      await onMerge(masterId, otherId);
    } catch (e: any) {
      setError(e?.message || 'Failed to merge contacts');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir='rtl'>
      <DialogTitle>מיזוג אנשי קשר</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', my: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ContactCard contact={left} />
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={loading}
              onClick={() => handleMerge(left.id, right.id)}
            >
              מיזוג לכאן
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ContactCard contact={right} />
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={loading}
              onClick={() => handleMerge(right.id, left.id)}
            >
              מיזוג לכאן
            </Button>
          </Box>
        </Box>
        {loading && <Box sx={{ textAlign: 'center', my: 2 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>ביטול</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeContactsModal; 